import React from 'react';
import ReactDOM from 'react-dom';
import { isEmpty, keys } from 'ramda';
import { BlobProvider, Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { store } from '../index';
import { failExportToInbox } from '../actions/pdfActions';
import { PDF_EXPORT } from '../constants/ApiEndpoints';
import {
  attachmentIconEncoded,
  channelHeaderText,
  dateHeaderText,
  senderHeaderText,
  updoxLogoEncoded,
} from '../constants/PdfConstants';
import { httpPost } from './HttpUtils';

class PdfBuilder {
  constructor() {
    // Registers the required fonts to build the PDF.
    Font.register(
      `${window.location.origin}/OpenSans-Regular.ttf`,
      { family: 'Open Sans' },
    );

    Font.register(
      `${window.location.origin}/OpenSans-Bold.ttf`,
      { family: 'Open Sans Bold' },
    );

    Font.register(
      `${window.location.origin}/OpenSans-Italic.ttf`,
      { family: 'Open Sans Italic' },
    );

    // Required to properly display emoji
    Font.registerEmojiSource({
      format: 'png',
      url: 'https://twemoji.maxcdn.com/2/72x72/',
    });

    // Spacing on each PDF page so it looks nice.
    this.pageMargins = {
      top: 50,
      bottom: 60,
      horizontal: 40,
    };

    // react-pdf styles for the PDF
    this.styles = StyleSheet.create({
      body: {
        paddingTop: this.pageMargins.top,
        paddingBottom: this.pageMargins.bottom,
        paddingHorizontal: this.pageMargins.horizontal,
        fontSize: 12,
        fontFamily: 'Open Sans',
        color: 'black',
      },
      title: {
        fontFamily: 'Open Sans Bold',
        fontSize: 24,
        textAlign: 'left',
        marginBottom: 0,
        paddingBottom: 0,
      },
      blueSpacer: {
        height: 10,
        width: 50,
        backgroundColor: '#00AEEF',
        marginTop: 5,
        marginBottom: 20,
      },
      text: {
        fontSize: 14,
        textAlign: 'left',
      },
      logo: {
        width: 'auto',
        height: 25,
        flexGrow: 0,
        flexShrink: 1,
      },
      pageNumber: {
        paddingTop: 8,
        flexGrow: 0,
        flexShrink: 1,
      },
      footer: {
        position: 'absolute',
        bottom: 20,
        left: this.pageMargins.horizontal,
        right: this.pageMargins.horizontal,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
      flexRow: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 5,
      },
      flexHeader: {
        fontFamily: 'Open Sans Bold',
        flexGrow: 0,
        flexShrink: 0,
      },
      flexContent: {
        paddingHorizontal: 5,
        flexGrow: 1,
        flexShrink: 1,
      },
      borderedText: {
        fontSize: 10,
        paddingTop: 10,
        paddingBottom: 4,
        paddingHorizontal: 5,
        marginTop: 20,
        marginBottom: 20,
        border: '1 solid gray',
      },
      messageContainer: {
        marginTop: 10,
        marginBottom: 15,
      },
      messageSender: {
        fontFamily: 'Open Sans Bold',
        flexGrow: 0,
        flexShrink: 0,
      },
      messageDate: {
        color: 'gray',
        paddingHorizontal: 15,
        flexGrow: 1,
        flexShrink: 1,
      },
      attachmentPreviewContainer: {
        width: '50%',
        marginBottom: 5,
      },
      attachmentPreviewHeader: {
        borderRight: '1 solid #003462',
        borderLeft: '1 solid #003462',
        backgroundColor: '#003462',
        paddingTop: 3,
        paddingHorizontal: 5,
        marginTop: 5,
        paddingBottom: 3,
        display: 'flex',
        flexDirection: 'row',
      },
      attachmentPreviewIcon: {
        height: 14,
        width: 14,
      },
      attachmentPreviewName: {
        paddingHorizontal: 15,
        color: 'white',
      },
      attachmentPreviewImage: {
        borderBottom: '1 solid gray',
        borderRight: '1 solid gray',
        borderLeft: '1 solid gray',
        width: 'auto',
        height: 100,
        paddingTop: 10,
        paddingBottom: 10,
        paddingHorizontal: 10,
      },
      nonContiguousMarker: {
        fontFamily: 'Open Sans Italic',
        fontSize: 12,
        paddingHorizontal: 5,
        color: 'gray',
        backgroundColor: '#F4F6F8',
      },
    });
  }

  renderDetailsRow = (header, content) => (
    <View style={this.styles.flexRow}>
      <Text style={this.styles.flexHeader}>{header}</Text>
      <Text style={this.styles.flexContent}>{content}</Text>
    </View>
  );

  renderHeader = (chatName, requestDate, requesterName) => (
    <View>
      <Text style={this.styles.title}>Messenger Export</Text>
      <View style={this.styles.blueSpacer}/>
      {this.renderDetailsRow(dateHeaderText, requestDate)}
      {this.renderDetailsRow(channelHeaderText, chatName)}
      {this.renderDetailsRow(senderHeaderText, requesterName)}
    </View>
  );

  renderAttachment = attachment => (
    <View style={this.styles.attachmentPreviewContainer} wrap={false}>
      <View style={this.styles.attachmentPreviewHeader}>
        <Image style={this.styles.attachmentPreviewIcon} src={attachmentIconEncoded} />
        <Text style={this.styles.attachmentPreviewName}>{attachment.fileName}</Text>
      </View>

      {attachment.displayUrl && attachment.mediaType !== 'image/gif'
      && <Image src={attachment.displayUrl} style={this.styles.attachmentPreviewImage}/>
      }
    </View>
  );

  renderNonContiguousMarker = key => (
    <Text key={key} style={this.styles.nonContiguousMarker}>
      Messages omitted between selections
    </Text>
  );

  renderMessage = (message) => {
    // If a message does not have a sender, do not show the message
    if (!message.senderName) {
      return null;
    }

    return (
      <View key={message.id} style={this.styles.messageContainer}>
        <View style={this.styles.flexRow}>
          <Text style={this.styles.messageSender}>{message.senderName}</Text>
          <Text style={this.styles.messageDate}>{message.timeSent}</Text>
        </View>
        {message.content && <Text style={this.styles.messageContent}>{message.content}</Text>}
        {message.attachment && this.renderAttachment(message.attachment)}
      </View>
    );
  };

  renderMessages = (messageIds, toExport) => {
    const renderableMessages = [];
    const toExportMessageIds = keys(toExport);

    let toExportPos = 0;
    let messagePos = messageIds.indexOf(toExportMessageIds[toExportPos]);

    while (toExportPos < toExportMessageIds.length) {
      const toExportKey = toExportMessageIds[toExportPos];
      const messageKey = messageIds[messagePos];

      if (toExportKey === messageKey) {
        renderableMessages.push(this.renderMessage(toExport[toExportKey]));
        toExportPos += 1;
        messagePos += 1;
      } else {
        renderableMessages.push(this.renderNonContiguousMarker(messageKey));
        messagePos = messageIds.indexOf(toExportMessageIds[toExportPos]);
      }
    }

    return renderableMessages;
  };

  renderFooter = () => (
    <View style={this.styles.footer} fixed>
      <Image style={this.styles.logo} src={updoxLogoEncoded} />
      <Text
        fixed
        style={this.styles.pageNumber}
        render={({ pageNumber, totalPages }) => (
          `Page ${pageNumber} of ${totalPages}`
        )}
      />
    </View>
  );

  /**
   * Call this to build a PDF Export
   *
   * @param chatName String value of the chat
   * @param messageIds List of IDs for all viewable messages in the current chat.
   * @param toExport List of messages/attachments to be inserted into the PDF.
   * @param requestDate Formatted string representation of the export date.
   * @param requesterName The formatted string name to be displayed in the PDF as the sender.
   *
   * @returns {*} A react-pdf document model of the built PDF.
   */
  buildPdfExport = ({
    chatName,
    messageIds,
    toExport,
    requestDate,
    requesterName,
  }) => {
    if (!messageIds || isEmpty(messageIds)) {
      return <Document><Page/></Document>;
    }

    return (
      <Document>
        <Page wrap style={this.styles.body}>
          {this.renderHeader(chatName, requestDate, requesterName)}

          <Text style={this.styles.borderedText}>Start of export</Text>

          {this.renderMessages(messageIds, toExport)}

          <Text style={this.styles.borderedText}>End of export</Text>

          {this.renderFooter()}
        </Page>
      </Document>
    );
  };

  /**
   * Look in index.html to see this element. If that ever changes, this will need updated.
   */
  static getPdfBlobDiv = () => document.getElementById('pdfBlob');

  static executeActionOnPdfBlob = (pdf, blobFn) => {
    const blob = (
      <BlobProvider document={pdf}>
        {blobFn}
      </BlobProvider>
    );

    ReactDOM.render(blob, PdfBuilder.getPdfBlobDiv());
  };

  /**
   * Call this to build the PDF blob and send it and the attachmentIDs to the backend.
   *
   * @param chatId
   * @param pdf The react-pdf document model
   * @param selectedAttachmentIds A list of attachment IDs to be added to the export
   */
  static sendPdfToInbox = (chatId, pdf, selectedAttachmentIds) => {
    const blobFn = ({ blob, url, loading, error }) => {
      if (!loading && blob && !isEmpty(blob) && !error) {
        const formData = new FormData();
        formData.append('chatId', chatId);
        formData.append('pdf', blob, `messenger-export-${Date.now()}.pdf`);

        if (selectedAttachmentIds && !isEmpty(selectedAttachmentIds)) {
          selectedAttachmentIds.forEach((attachmentId) => {
            formData.append('attachmentIds', attachmentId);
          });
        }

        httpPost(PDF_EXPORT, formData)
          .catch((err) => {
            console.error(err);
            store.dispatch(failExportToInbox(store.getState().chats[chatId]));
          })
          .finally(() => {
            ReactDOM.unmountComponentAtNode(PdfBuilder.getPdfBlobDiv());
          });
      }

      // Render nothing to the screen. This is a react render function.
      return null;
    };

    return PdfBuilder.executeActionOnPdfBlob(pdf, blobFn);
  };
}

export default PdfBuilder;
